import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { fnoReconApi } from '@/shared/api';
import { format } from 'date-fns';

export function FnoReconListPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['fno-recon-jobs'],
    queryFn: () => fnoReconApi.list(),
    refetchInterval: 15_000,
  });

  const deleteMutation = useMutation({
    mutationFn: (job_id: string) => fnoReconApi.delete(job_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fno-recon-jobs'] });
    },
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-axis-ink">F&amp;O Reconciliation</h1>
          <p className="text-sm text-axis-ash mt-1">
            Cross-check portfolio F&amp;O positions against DP transaction statements.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link to="/fno-recon/new" className="btn-primary">
            <Plus className="w-4 h-4" />
            New reconciliation
          </Link>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-axis-cloud-100 text-axis-ash text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Job ID</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Client ID</th>
              <th className="text-right px-4 py-3">Match Rate</th>
              <th className="text-right px-4 py-3">Mismatches</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-axis-cloud-200">
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-axis-ash">Loading…</td></tr>
            ) : !data?.rows?.length ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-axis-ash">
                No F&amp;O reconciliations yet.{' '}
                <Link to="/fno-recon/new" className="text-axis-maroon underline">Start one</Link>.
              </td></tr>
            ) : data.rows.map((job) => (
              <tr
                key={job.id}
                onClick={() => navigate(`/fno-recon/${job.id}`)}
                className="hover:bg-axis-cloud-50 cursor-pointer"
              >
                <td className="px-4 py-3 font-mono text-xs">{job.id.slice(0, 12)}…</td>
                <td className="px-4 py-3">
                  <span className={
                    job.status === 'completed'
                      ? 'pill bg-emerald-100 text-emerald-800 border border-emerald-200'
                      : job.status === 'failed'
                        ? 'pill bg-rose-100 text-rose-800 border border-rose-200'
                        : 'pill bg-axis-cloud-100 text-axis-ash border border-axis-cloud-200'
                  }>{job.status}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">
                  {(job as any).summary?.overall?.pf_client_id ?? (job as any).summary?.overall?.bo_client_id ?? '—'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-xs">
                  {(job as any).summary?.overall?.match_rate_pct != null
                    ? <span style={{ color: (job as any).summary.overall.match_rate_pct >= 95 ? 'var(--status-ok)' : (job as any).summary.overall.match_rate_pct >= 70 ? 'var(--status-warn)' : 'var(--status-err)' }}>
                        {(job as any).summary.overall.match_rate_pct.toFixed(1)}%
                      </span>
                    : '—'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-xs">
                  {(job as any).summary?.overall?.mismatch_count != null
                    ? <span style={{ color: (job as any).summary.overall.mismatch_count > 0 ? 'var(--status-warn)' : 'var(--status-ok)' }}>
                        {(job as any).summary.overall.mismatch_count.toLocaleString('en-IN')}
                      </span>
                    : '—'}
                </td>
                <td className="px-4 py-3 text-xs text-axis-ash">
                  {format(new Date(job.created_at), 'd MMM yyyy, HH:mm:ss.SSS')}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`Delete job ${job.id}?\n\nThis permanently removes the job and its data.`)) {
                        deleteMutation.mutate(job.id);
                      }
                    }}
                    disabled={deleteMutation.isPending && deleteMutation.variables === job.id}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition disabled:opacity-50 opacity-40 hover:opacity-100"
                    style={{
                      background: 'color-mix(in srgb, var(--status-err) 8%, transparent)',
                      color: 'var(--status-err)',
                      border: '1px solid color-mix(in srgb, var(--status-err) 25%, transparent)',
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
